import typeforce from '@exodus/typeforce';
import { types } from './types.js';
import { getSupportedPurposes } from './utils.js';
import { UnknownWalletAccountError, UnsupportedAssetError, UnsupportedAssetSourceError, } from './errors.js';
const MODULE_ID = 'assetSources';
class AssetSources {
    #assetsAtom;
    #walletAccountsAtom;
    #availableAssetNamesByWalletAccountAtom;
    constructor({ assetsAtom, walletAccountsAtom, availableAssetNamesByWalletAccountAtom, }) {
        this.#assetsAtom = assetsAtom;
        this.#walletAccountsAtom = walletAccountsAtom;
        this.#availableAssetNamesByWalletAccountAtom = availableAssetNamesByWalletAccountAtom;
    }
    #getAsset = async (assetName) => {
        const { value: assets } = await this.#assetsAtom.get();
        const asset = assets[assetName];
        if (!asset) {
            throw new UnsupportedAssetError(assetName);
        }
        return asset;
    };
    #getWalletAccount = async (walletAccount) => {
        const all = await this.#walletAccountsAtom.get();
        if (!all[walletAccount]) {
            throw new UnknownWalletAccountError(walletAccount);
        }
        return all[walletAccount];
    };
    getSupportedPurposes = async ({ walletAccount, assetName }) => {
        typeforce(types.assetSource, { walletAccount, assetName }, true);
        const walletAccountInstance = await this.#getWalletAccount(walletAccount);
        return getSupportedPurposes({
            asset: await this.#getAsset(assetName),
            walletAccount: walletAccountInstance,
        });
    };
    getDefaultPurpose = async ({ walletAccount, assetName }) => {
        typeforce(types.assetSource, { walletAccount, assetName }, true);
        const [defaultPurpose] = await this.getSupportedPurposes({ walletAccount, assetName });
        if (typeof defaultPurpose !== 'number')
            throw new UnsupportedAssetSourceError({ walletAccount, assetName });
        return defaultPurpose;
    };
    isSupported = async ({ walletAccount, assetName }) => {
        typeforce(types.assetSource, { walletAccount, assetName }, true);
        const { [walletAccount]: availableAssetNames } = await this.#availableAssetNamesByWalletAccountAtom.get();
        return Boolean(availableAssetNames?.includes(assetName));
    };
}
const createAssetSources = (opts) => new AssetSources(opts);
const assetSourcesDefinition = {
    id: MODULE_ID,
    type: 'module',
    factory: createAssetSources,
    dependencies: ['assetsAtom', 'walletAccountsAtom', 'availableAssetNamesByWalletAccountAtom'],
    public: true,
};
export default assetSourcesDefinition;
